import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { MINHAS_NOTIFICACOES, CONTADOR_NOTIFICACOES } from "@/graphql/notificacoes/queries";
import { MARCAR_NOTIFICACAO_LIDA, MARCAR_TODAS_NOTIFICACOES_LIDAS } from "@/graphql/notificacoes/mutations";
import { useAuth } from "@/contexts/AuthContext";

// Notificação local (toasts efêmeros)
export interface LocalNotification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

// Notificação do backend (persistidas)
export interface ServerNotification {
  id: number;
  tipo: string;
  titulo: string;
  mensagem: string;
  lida: boolean;
  dataLeitura: string | null;
  entidadeTipo: string | null;
  entidadeId: number | null;
  metadata: Record<string, unknown> | null;
  criadoEm: string;
}

interface NotificationContextType {
  // Backend notifications
  serverNotifications: ServerNotification[];
  unreadCount: number;
  loadingNotifications: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetchNotifications: () => void;
  fetchMoreNotifications: () => void;
  hasMore: boolean;

  // Local toast notifications (kept for backward compatibility)
  localNotifications: LocalNotification[];
  addNotification: (notification: Omit<LocalNotification, "id" | "timestamp" | "read">) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  showSuccess: (title: string, message: string) => void;
  showError: (title: string, message: string) => void;
  showWarning: (title: string, message: string) => void;
  showInfo: (title: string, message: string) => void;

  // Combined for backward compat
  notifications: LocalNotification[];
  unreadNotificationsCount: number;
  hasNotifications: boolean;
  hasUnreadNotifications: boolean;
  markNotificationAsRead: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { isAuthenticated } = useAuth();
  const [localNotifications, setLocalNotifications] = useState<LocalNotification[]>([]);
  const [page, setPage] = useState(1);

  // Backend: contador com polling de 30s
  const { data: contadorData } = useQuery(CONTADOR_NOTIFICACOES, {
    pollInterval: 30000,
    fetchPolicy: "network-only",
    skip: !isAuthenticated,
  });

  // Backend: lista de notificações
  const {
    data: notifData,
    loading: loadingNotifications,
    refetch: refetchNotifications,
    fetchMore,
  } = useQuery(MINHAS_NOTIFICACOES, {
    variables: { pagination: { page: 1, limit: 20 } },
    fetchPolicy: "cache-and-network",
    skip: !isAuthenticated,
  });

  const [marcarLidaMutation] = useMutation(MARCAR_NOTIFICACAO_LIDA);
  const [marcarTodasMutation] = useMutation(MARCAR_TODAS_NOTIFICACOES_LIDAS);

  const serverNotifications: ServerNotification[] = notifData?.minhasNotificacoes?.items || [];
  const hasMore: boolean = notifData?.minhasNotificacoes?.hasMore || false;
  const unreadCount: number = contadorData?.contadorNotificacoes?.naoLidas || 0;

  const markAsRead = useCallback(async (id: number) => {
    await marcarLidaMutation({
      variables: { id },
      refetchQueries: [
        { query: CONTADOR_NOTIFICACOES },
        { query: MINHAS_NOTIFICACOES, variables: { pagination: { page: 1, limit: 20 } } },
      ],
    });
  }, [marcarLidaMutation]);

  const markAllAsRead = useCallback(async () => {
    await marcarTodasMutation({
      refetchQueries: [
        { query: CONTADOR_NOTIFICACOES },
        { query: MINHAS_NOTIFICACOES, variables: { pagination: { page: 1, limit: 20 } } },
      ],
    });
  }, [marcarTodasMutation]);

  const fetchMoreNotifications = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMore({
      variables: { pagination: { page: nextPage, limit: 20 } },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          minhasNotificacoes: {
            ...fetchMoreResult.minhasNotificacoes,
            items: [
              ...prev.minhasNotificacoes.items,
              ...fetchMoreResult.minhasNotificacoes.items,
            ],
          },
        };
      },
    });
  }, [fetchMore, page]);

  // Local notification helpers (backward compat)
  const addNotification = useCallback(
    (notification: Omit<LocalNotification, "id" | "timestamp" | "read">) => {
      const newNotification: LocalNotification = {
        ...notification,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
        read: false,
      };
      setLocalNotifications((prev) => [newNotification, ...prev].slice(0, 50));
    },
    []
  );

  const markNotificationAsRead = useCallback((id: string) => {
    setLocalNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif))
    );
  }, []);

  const removeNotification = useCallback((id: string) => {
    setLocalNotifications((prev) => prev.filter((notif) => notif.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setLocalNotifications([]);
  }, []);

  const showSuccess = useCallback(
    (title: string, message: string) => addNotification({ type: "success", title, message }),
    [addNotification]
  );
  const showError = useCallback(
    (title: string, message: string) => addNotification({ type: "error", title, message }),
    [addNotification]
  );
  const showWarning = useCallback(
    (title: string, message: string) => addNotification({ type: "warning", title, message }),
    [addNotification]
  );
  const showInfo = useCallback(
    (title: string, message: string) => addNotification({ type: "info", title, message }),
    [addNotification]
  );

  const value: NotificationContextType = {
    // Backend
    serverNotifications,
    unreadCount,
    loadingNotifications,
    markAsRead,
    markAllAsRead,
    refetchNotifications,
    fetchMoreNotifications,
    hasMore,

    // Local
    localNotifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,

    // Backward compat
    notifications: localNotifications,
    unreadNotificationsCount: unreadCount,
    hasNotifications: serverNotifications.length > 0 || localNotifications.length > 0,
    hasUnreadNotifications: unreadCount > 0,
    markNotificationAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};
